import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem } from "namirasoft-core";
import { MaintenancePlanEveryDistanceUnit, MaintenancePlanInputRow, MaintenancePlanRow, MaintenancePlanEveryTimeUnit, NamirasoftERPMetaDatabase, ServiceTypeRow, VehicleRow } from "namirasoft-erp";
import { NamirasoftNotificationSenderMetaDatabase, TopicRow } from "namirasoft-notification-sender";
import { NSBox, NSBoxDateTime, NSBoxEntity, NSBoxEnum, NSBoxInteger, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpEntities } from "../ErpEntities";
import { ErpLayout } from '../ErpLayout';
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";

interface Props extends NSARouterMakerResult, ErpRouterProps { isEdit: boolean; }

export default class MaintenancePlanEditPage extends Component<Props>
{
    Vehicle = createRef<NSBoxEntity<VehicleRow>>();
    ServiceType = createRef<NSBoxEntity<ServiceTypeRow>>();
    Topic = createRef<NSBoxEntity<TopicRow>>();
    StartsAt = createRef<NSBoxDateTime>();
    EveryDistanceValue = createRef<NSBoxInteger>();
    EveryDistanceUnit = createRef<NSBoxEnum<MaintenancePlanEveryDistanceUnit>>();
    EveryTimeValue = createRef<NSBoxInteger>();
    EveryTimeUnit = createRef<NSBoxEnum<MaintenancePlanEveryTimeUnit>>();
    Description = createRef<NSBoxTextArea>();

    override render()
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let sender_db = new NamirasoftNotificationSenderMetaDatabase(this.props.account.token_manager, console.error);
        let sender = sender_db.getServer();

        return (
            <ErpLayout<MaintenancePlanRow, MaintenancePlanInputRow>
                {...this.props}
                mode={ErpScopeMode.Select}
                fields={[ErpScopeField.Company, ErpScopeField.Branch]}
                pages={{
                    entity: ErpEntities.maintenance_plan(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): MaintenancePlanInputRow => ({
                                        company_id: ErpScope.getCompanyId() ?? "",
                                        branch_id: ErpScope.getBranchId() ?? "",
                                        vehicle_id: this.Vehicle.current?.getValueOne() ?? "",
                                        service_type_id: this.ServiceType.current?.getValueOne() ?? "",
                                        topic_id: this.Topic.current?.getValueOne(false) ?? null,
                                        starts_at: new Date(this.StartsAt.current?.getValue() ?? new Date().toISOString()),
                                        every_distance_value: this.EveryDistanceValue.current?.getValue() ?? 0,
                                        every_distance_unit: this.EveryDistanceUnit.current?.getValueEnumOne(MaintenancePlanEveryDistanceUnit, MaintenancePlanEveryDistanceUnit.KM) ?? MaintenancePlanEveryDistanceUnit.KM,
                                        every_time_value: this.EveryTimeValue.current?.getValue() ?? 0,
                                        every_time_unit: this.EveryTimeUnit.current?.getValueEnumOne(MaintenancePlanEveryTimeUnit, MaintenancePlanEveryTimeUnit.Month) ?? MaintenancePlanEveryTimeUnit.Month,
                                        description: this.Description.current?.getValue() ?? null,
                                        maintenance_plan_category: [],
                                        maintenance_plan_tag: [],
                                        maintenance_plan_field: [],
                                    }),
                                    setRow: (row: MaintenancePlanRow) =>
                                    {
                                        ErpScope.set({ company_id: row.company_id, branch_id: row.branch_id });
                                        this.Vehicle.current?.setValueOne(row.vehicle_id);
                                        this.ServiceType.current?.setValueOne(row.service_type_id);
                                        this.Topic.current?.setValueOne(row.topic_id);
                                        this.StartsAt.current?.setValue(new Date(row.starts_at).toISOString());
                                        this.EveryDistanceValue.current?.setValue(row.every_distance_value);
                                        this.EveryDistanceUnit.current?.setValueOne(row.every_distance_unit);
                                        this.EveryTimeValue.current?.setValue(row.every_time_value);
                                        this.EveryTimeUnit.current?.setValueOne(row.every_time_unit);
                                        this.Description.current?.setValue(row.description);
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxEntity<VehicleRow>
                        ref={this.Vehicle} title="Vehicle" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-vehicle-id" }} required multiple={false}
                        table_name="vehicle" table={db.tables.vehicle}
                        list={(filters, page, size, sorts) =>
                        {
                            let f: FilterItem[] = filters ? [...filters] : [];
                            f = ErpScope.pushFilter(f, db.tables.vehicle, "company_id", ErpScope.getCompanyId());
                            f = ErpScope.pushFilter(f, db.tables.vehicle, "branch_id", ErpScope.getBranchId());
                            return this.props.servers.erp.vehicle.List(f, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ServiceTypeRow>
                        ref={this.ServiceType} title="Service Type" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-service-type-id" }} required multiple={false}
                        table_name="service_type" table={db.tables.service_type}
                        list={(filters, page, size, sorts) =>
                        {
                            let f: FilterItem[] = filters ? [...filters] : [];
                            f = ErpScope.pushFilter(f, db.tables.service_type, "company_id", ErpScope.getCompanyId());
                            f = ErpScope.pushFilter(f, db.tables.service_type, "branch_id", ErpScope.getBranchId());
                            return this.props.servers.erp.service_type.List(f, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<TopicRow>
                        ref={this.Topic} title="Topic" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-topic-id" }} required={false} multiple={false}
                        table_name="topic" table={sender_db.tables.topic}
                        list={(filters, page, size, sorts) => sender.topic.List(filters, page, size, sorts, null)}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxDateTime ref={this.StartsAt} required defaultValue={this.props.isEdit ? undefined : new Date()} title="Starts At" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-starts-at" }} style={{ width: NSBox.width.double }} />
                    <NSRow style={{ gap: 8 }}>
                        <NSBoxInteger ref={this.EveryDistanceValue} required title="Every Distance Value" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-every-distance-value" }} style={{ width: NSBox.width.one }} />
                        <NSBoxEnum<MaintenancePlanEveryDistanceUnit>
                            ref={this.EveryDistanceUnit} title="Every Distance Unit" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-every-distance-unit" }} required multiple={false}
                            getEnumObject={() => MaintenancePlanEveryDistanceUnit}
                            style={{ width: NSBox.width.one }}
                        />
                    </NSRow>
                    <NSRow style={{ gap: 8 }}>
                        <NSBoxInteger ref={this.EveryTimeValue} required title="Every Time Value" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-every-time-value" }} style={{ width: NSBox.width.one }} />
                        <NSBoxEnum<MaintenancePlanEveryTimeUnit>
                            ref={this.EveryTimeUnit} title="Every Time Unit" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-every-time-unit" }} required multiple={false}
                            getEnumObject={() => MaintenancePlanEveryTimeUnit}
                            defaultValue={MaintenancePlanEveryTimeUnit.Month}
                            style={{ width: NSBox.width.one }}
                        />
                    </NSRow>
                    <NSBoxTextArea ref={this.Description} required={false} title="Description" info={{ link: "https://namirasoft.com/erp/console-guide/maintenance-plan/#field-description" }} style={{ width: NSBox.width.double }} />
                </NSColumn>
            </ErpLayout>
        );
    }
}